(() => {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const header = $('[data-header]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  const menuItems = $$('[data-menu-item]');
  const closeMenus = (except = null) => {
    menuItems.forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      $('[data-menu-button]', item)?.setAttribute('aria-expanded', 'false');
    });
  };

  menuItems.forEach((item) => {
    const button = $('[data-menu-button]', item);
    if (!button) return;

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !item.classList.contains('is-open');
      closeMenus(item);
      item.classList.toggle('is-open', willOpen);
      button.setAttribute('aria-expanded', String(willOpen));
    });

    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) {
        item.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-menu-item]')) closeMenus();
  });

  // Mobile drawer: event delegation + independent scroll container.
  // Delegation keeps controls working even when Shopify re-renders the header section.
  let lastFocus = null;
  let drawerScrollY = 0;
  const getDrawer = () => $('[data-drawer]');

  const getAccordionPanel = (button) => {
    const panelId = button?.getAttribute('aria-controls');
    return panelId ? document.getElementById(panelId) : null;
  };

  const setMobileAccordion = (button, open) => {
    const panel = getAccordionPanel(button);
    if (!button || !panel) return;

    // Keep the three medium service groups mutually exclusive on phones.
    // This prevents a very tall stack while still allowing the drawer itself to scroll.
    if (open && button.classList.contains('mobile-service-group__toggle')) {
      const groupsRoot = button.closest('.mobile-services__groups');
      if (groupsRoot) {
        $$(':scope > .mobile-service-group > [data-mobile-accordion-button]', groupsRoot).forEach((sibling) => {
          if (sibling === button) return;
          const siblingPanel = getAccordionPanel(sibling);
          sibling.setAttribute('aria-expanded', 'false');
          sibling.classList.remove('is-open');
          const siblingIcon = $('.mobile-nav__toggle-icon', sibling);
          if (siblingIcon) siblingIcon.textContent = '+';
          if (siblingPanel) siblingPanel.hidden = true;
        });
      }
    }

    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    button.classList.toggle('is-open', open);
    const icon = $('.mobile-nav__toggle-icon', button);
    if (icon) icon.textContent = open ? '−' : '+';

    if (!open) {
      $$('[data-mobile-accordion-button]', panel).forEach((nestedButton) => {
        const nestedPanel = getAccordionPanel(nestedButton);
        nestedButton.setAttribute('aria-expanded', 'false');
        nestedButton.classList.remove('is-open');
        const nestedIcon = $('.mobile-nav__toggle-icon', nestedButton);
        if (nestedIcon) nestedIcon.textContent = '+';
        if (nestedPanel) nestedPanel.hidden = true;
      });
    }

    if (open) {
      window.requestAnimationFrame(() => {
        try { button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' }); } catch (_) {}
      });
    }
  };

  const resetMobileAccordions = (drawer = getDrawer()) => {
    if (!drawer) return;
    $$('[data-mobile-accordion-button]', drawer).forEach((button) => {
      const panel = getAccordionPanel(button);
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('is-open');
      const icon = $('.mobile-nav__toggle-icon', button);
      if (icon) icon.textContent = '+';
      if (panel) panel.hidden = true;
    });
  };

  const lockPageForDrawer = () => {
    const html = document.documentElement;
    const body = document.body;
    drawerScrollY = window.scrollY || window.pageYOffset || 0;
    html.classList.add('mobile-drawer-open');
    html.style.overflow = 'hidden';
    body.classList.add('menu-open');
    body.style.position = 'fixed';
    body.style.top = `-${drawerScrollY}px`;
    body.style.width = '100%';
  };

  const unlockPageFromDrawer = () => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.remove('mobile-drawer-open');
    html.style.overflow = '';
    body.classList.remove('menu-open');
    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    window.scrollTo(0, drawerScrollY);
  };

  const closeDrawer = ({ restoreFocus = true } = {}) => {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockPageFromDrawer();
    if (restoreFocus && lastFocus && document.contains(lastFocus)) {
      try { lastFocus.focus({ preventScroll: true }); } catch (_) { lastFocus.focus(); }
    }
  };

  const openDrawer = (trigger = null) => {
    const drawer = getDrawer();
    if (!drawer) return;
    lastFocus = trigger || document.activeElement;
    resetMobileAccordions(drawer);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockPageForDrawer();
    const panel = $('.drawer__panel', drawer);
    if (panel) panel.scrollTop = 0;
    window.requestAnimationFrame(() => $('[data-drawer-close]', drawer)?.focus({ preventScroll: true }));
  };

  // One delegated click handler prevents stale/broken listeners in Shopify preview
  // and makes taps on the text/icon inside a button behave exactly like taps on the button.
  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-drawer-open]');
    if (openButton) {
      event.preventDefault();
      openDrawer(openButton);
      return;
    }

    const drawer = getDrawer();
    const closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton && drawer?.contains(closeButton)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const accordionButton = event.target.closest('[data-mobile-accordion-button]');
    if (accordionButton && drawer?.contains(accordionButton)) {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = accordionButton.getAttribute('aria-expanded') !== 'true';
      setMobileAccordion(accordionButton, willOpen);
      return;
    }

    if (drawer?.classList.contains('is-open') && event.target === drawer) {
      closeDrawer();
      return;
    }

    // Never prevent a real link from navigating. Delaying the visual drawer close
    // avoids Safari cancelling/losing taps when the off-canvas panel transforms immediately.
    const drawerLink = event.target.closest('a[href]');
    if (drawerLink && drawer?.contains(drawerLink)) {
      window.setTimeout(() => closeDrawer({ restoreFocus: false }), 160);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getDrawer();
    if (event.key === 'Escape') {
      closeDrawer();
      closeMenus();
      const quickContact = $('[data-quick-contact]');
      if (quickContact?.classList.contains('is-open')) {
        $('[data-quick-contact-toggle]', quickContact)?.click();
      }
      return;
    }

    if (event.key === 'Tab' && drawer?.classList.contains('is-open')) {
      const focusable = $$('a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])', drawer)
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  const quickContact = $('[data-quick-contact]');
  if (quickContact) {
    const launcher = $('[data-quick-contact-launcher]', quickContact);
    const toggle = $('[data-quick-contact-toggle]', quickContact);
    const actions = $('[data-quick-contact-actions]', quickContact);

    const setQuickContact = (open) => {
      quickContact.classList.toggle('is-open', open);
      toggle?.setAttribute('aria-expanded', String(open));
      toggle?.setAttribute('aria-label', open ? 'סגירת אפשרויות יצירת קשר' : 'פתיחת אפשרויות יצירת קשר');
      actions?.setAttribute('aria-hidden', String(!open));
      launcher?.classList.toggle('is-active', open);
    };

    toggle?.addEventListener('click', () => setQuickContact(!quickContact.classList.contains('is-open')));

    document.addEventListener('click', (event) => {
      if (quickContact.classList.contains('is-open') && !quickContact.contains(event.target)) {
        setQuickContact(false);
      }
    });
  }

  $$('[data-video-tabs]').forEach((tabs) => {
    $$('[data-video-tab]', tabs).forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.videoTab;
        $$('[data-video-tab]', tabs).forEach((tab) => tab.setAttribute('aria-selected', String(tab === button)));
        $$('[data-video-panel]', tabs).forEach((panel) => {
          const active = panel.dataset.videoPanel === id;
          panel.classList.toggle('is-active', active);
          if (active) {
            const frame = $('iframe[data-src]', panel);
            if (frame) {
              frame.src = frame.dataset.src;
              frame.removeAttribute('data-src');
            }
          }
        });
      });
    });
  });

  $$('[data-image-swap]').forEach((swap) => {
    const figures = $$('figure', swap);
    const count = $('[data-swap-count]', swap);
    let index = 0;
    let timer = null;

    const show = (nextIndex) => {
      index = (nextIndex + figures.length) % figures.length;
      figures.forEach((figure, figureIndex) => {
        const active = figureIndex === index;
        figure.classList.toggle('is-active', active);
        figure.setAttribute('aria-hidden', String(!active));
        $$('a, button', figure).forEach((control) => {
          if (active) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
      });
      if (count) count.textContent = `${index + 1} / ${figures.length}`;
    };

    $('[data-swap-prev]', swap)?.addEventListener('click', () => show(index - 1));
    $('[data-swap-next]', swap)?.addEventListener('click', () => show(index + 1));
    show(0);

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && figures.length > 1) {
      timer = window.setInterval(() => show(index + 1), 6500);
      swap.addEventListener('mouseenter', () => window.clearInterval(timer));
    }
  });

  $$('[data-results-carousel]').forEach((carousel) => {
    const viewport = $('[data-results-viewport]', carousel);
    const cards = $$('.result-card', carousel);
    let index = 0;
    if (!viewport || !cards.length) return;

    const showResult = (nextIndex) => {
      index = Math.max(0, Math.min(cards.length - 1, nextIndex));
      cards[index].scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    };

    $('[data-results-prev]', carousel)?.addEventListener('click', () => showResult(index - 1));
    $('[data-results-next]', carousel)?.addEventListener('click', () => showResult(index + 1));
  });

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible', 'visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 })
    : null;

  $$('.reveal').forEach((element) => {
    if (observer) observer.observe(element);
    else element.classList.add('is-visible', 'visible');
  });


  const accessibilityWidget = $('[data-a11y-widget]');
  if (accessibilityWidget) {
    const toggle = $('[data-a11y-toggle]', accessibilityWidget);
    const panel = $('[data-a11y-panel]', accessibilityWidget);
    const close = $('[data-a11y-close]', accessibilityWidget);
    const actionButtons = $$('[data-a11y-action]', accessibilityWidget);
    const storageKey = 'rishumpA11yV1';
    const defaults = { scale: 1, grayscale: false, contrast: false, invert: false, light: false, links: false, readable: false };
    let state = { ...defaults };

    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      state = { ...defaults, ...saved };
    } catch (_) {}

    const persist = () => {
      try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) {}
    };

    const applyA11y = () => {
      const html = document.documentElement;
      html.style.fontSize = `${Math.round(16 * Math.max(.9, Math.min(1.3, Number(state.scale) || 1)) * 10) / 10}px`;
      ['grayscale','contrast','invert','light','links','readable'].forEach((key) => {
        const className = key === 'contrast' ? 'a11y-high-contrast' : `a11y-${key}`;
        html.classList.toggle(className, Boolean(state[key]));
      });
      actionButtons.forEach((button) => {
        const key = button.dataset.a11yAction;
        if (!['text-up','text-down','reset'].includes(key)) {
          const active = Boolean(state[key]);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        }
      });
    };

    const setPanel = (open) => {
      accessibilityWidget.classList.toggle('is-open', open);
      toggle?.setAttribute('aria-expanded', String(open));
      panel?.setAttribute('aria-hidden', String(!open));
      if (open) $('[data-a11y-action]', panel)?.focus();
    };

    toggle?.addEventListener('click', () => setPanel(!accessibilityWidget.classList.contains('is-open')));
    close?.addEventListener('click', () => setPanel(false));
    document.addEventListener('click', (event) => {
      if (accessibilityWidget.classList.contains('is-open') && !accessibilityWidget.contains(event.target)) setPanel(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && accessibilityWidget.classList.contains('is-open')) setPanel(false);
    });

    actionButtons.forEach((button) => button.addEventListener('click', () => {
      const action = button.dataset.a11yAction;
      if (action === 'text-up') state.scale = Math.min(1.3, (Number(state.scale) || 1) + .1);
      else if (action === 'text-down') state.scale = Math.max(.9, (Number(state.scale) || 1) - .1);
      else if (action === 'reset') state = { ...defaults };
      else if (Object.prototype.hasOwnProperty.call(state, action)) {
        const nextValue = !state[action];
        if (nextValue && ['grayscale','contrast','invert','light'].includes(action)) {
          ['grayscale','contrast','invert','light'].forEach((key) => { state[key] = false; });
        }
        state[action] = nextValue;
      }
      applyA11y();
      persist();
    }));

    applyA11y();
  }
})();
